import React from 'react';
import { CalendarDays, Clock, Download, Mail, Phone, Ticket } from 'lucide-react';
import usePageData from '../helpers/usePageData';
import '../css/restaurant.css';

const RestaurantPage = () => {
    const { data: pageData, loading } = usePageData('restaurant');

    if (loading || !pageData) return null;

    const sections = pageData.sections || [];
    const infos = Object.entries(pageData.infos || {});
    const tarifs = pageData.tarifs || [];
    // Several numbers can be entered in the admin, separated by "/"
    const phones = (pageData.phone || '').split('/').map(p => p.trim()).filter(Boolean);

    return (
        <div className="restaurant-page">
            {/* Hero */}
            <section className="restaurant-hero">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7">
                            <p className="restaurant-eyebrow">Le restaurant du MACTe</p>
                            <h1 className="restaurant-title">{pageData.hero?.title}</h1>
                            {pageData.hero?.subtitle && (
                                <p className="restaurant-subtitle">{pageData.hero.subtitle}</p>
                            )}
                            {pageData.highlight && (
                                <p className="restaurant-highlight">
                                    <CalendarDays size={20} className="me-2 flex-shrink-0" />
                                    {pageData.highlight}
                                </p>
                            )}
                            <div className="d-flex flex-wrap gap-3 mt-4">
                                {pageData.bookingUrl && (
                                    <a href={pageData.bookingUrl} target="_blank" rel="noopener noreferrer" className="restaurant-btn restaurant-btn--primary">
                                        <Ticket size={18} className="me-2" /> Réserver
                                    </a>
                                )}
                                {pageData.email && (
                                    <a href={`mailto:${pageData.email}`} className="restaurant-btn restaurant-btn--outline">
                                        <Mail size={18} className="me-2" /> Nous écrire
                                    </a>
                                )}
                            </div>
                        </div>
                        {pageData.heroImage && (
                            <div className="col-lg-5 text-center">
                                <img src={pageData.heroImage} alt={pageData.highlight || pageData.hero?.title} className="restaurant-hero-image" />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Content sections */}
            {sections.map((section, index) => (
                <section key={index} className={`restaurant-section ${index % 2 ? 'restaurant-section--alt' : ''}`}>
                    <div className="container">
                        <div className="row g-5 align-items-center">
                            <div className={section.image ? 'col-lg-7' : 'col-lg-10'}>
                                {section.title && <h2 className="restaurant-section-title">{section.title}</h2>}
                                {(section.paragraphs || []).map((p, i) => (
                                    <p key={i} className="restaurant-text">{p}</p>
                                ))}
                                {section.content && <p className="restaurant-text">{section.content}</p>}
                                {Array.isArray(section.list) && section.list.length > 0 && (
                                    <ul className="restaurant-list">
                                        {section.list.map((item, i) => <li key={i}>{item}</li>)}
                                    </ul>
                                )}
                            </div>
                            {section.image && (
                                <div className="col-lg-5 text-center">
                                    <img src={section.image} alt={section.title} className="restaurant-section-image" loading="lazy" />
                                </div>
                            )}
                        </div>
                        {Array.isArray(section.profiles) && section.profiles.length > 0 && (
                            <div className="row g-4 mt-2">
                                {section.profiles.map((profile, i) => (
                                    <div key={i} className="col-lg-4">
                                        <div className="restaurant-profile">
                                            <h3 className="restaurant-profile-name">{profile.name}</h3>
                                            <p className="restaurant-profile-bio">{profile.bio}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            ))}

            {/* Formules */}
            {tarifs.length > 0 && (
                <section className="restaurant-section restaurant-formules">
                    <div className="container">
                        <h2 className="restaurant-section-title text-white">Choisissez votre formule</h2>
                        <div className="row g-4 mt-1">
                            {tarifs.map((tarif, i) => (
                                <div key={i} className="col-md-6 col-xl-3">
                                    <div className="restaurant-card">
                                        <h3 className="restaurant-card-title">{tarif.title}</h3>
                                        <p className="restaurant-card-price">{tarif.price}</p>
                                        {tarif.note && <p className="restaurant-card-note">{tarif.note}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {pageData.tarifsNote && <p className="restaurant-formules-note">{pageData.tarifsNote}</p>}
                    </div>
                </section>
            )}

            {/* Infos pratiques + contact */}
            <section className="restaurant-section">
                <div className="container">
                    <div className="row g-5">
                        {infos.length > 0 && (
                            <div className="col-lg-7">
                                <h2 className="restaurant-section-title">Informations pratiques</h2>
                                <dl className="restaurant-infos">
                                    {infos.map(([label, value]) => (
                                        <div key={label} className="restaurant-info">
                                            <dt><Clock size={18} className="me-2" />{label}</dt>
                                            <dd>{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                                {pageData.legal && <p className="restaurant-legal">{pageData.legal}</p>}
                            </div>
                        )}
                        <div className="col-lg-5">
                            <div className="restaurant-contact">
                                <h2 className="restaurant-section-title">Réservez votre expérience</h2>
                                {pageData.bookingUrl && (
                                    <div className="restaurant-booking">
                                        <a href={pageData.bookingUrl} target="_blank" rel="noopener noreferrer" className="restaurant-btn restaurant-btn--primary">
                                            <Ticket size={18} className="me-2" /> Réserver sur la billetterie
                                        </a>
                                        {pageData.bookingQr && (
                                            <img src={pageData.bookingQr} alt="QR code de la billetterie Ichali" className="restaurant-booking-qr" loading="lazy" />
                                        )}
                                    </div>
                                )}
                                {phones.map(phone => (
                                    <a key={phone} href={`tel:${phone.replace(/\s/g, '')}`} className="restaurant-contact-line"><Phone size={18} className="me-2" />{phone}</a>
                                ))}
                                {pageData.email && (
                                    <a href={`mailto:${pageData.email}`} className="restaurant-contact-line"><Mail size={18} className="me-2" />{pageData.email}</a>
                                )}
                                <div className="d-flex flex-wrap gap-3 mt-4">
                                    {pageData.brochureUrl && (
                                        <a href={pageData.brochureUrl} target="_blank" rel="noopener noreferrer" className="restaurant-btn restaurant-btn--dark-outline">
                                            <Download size={18} className="me-2" /> Télécharger la plaquette
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RestaurantPage;
